import React, { useState, useEffect, useRef } from "react";
import TextField from "../../components/customMui/TextField";
import LoadingButton from "../../components/customMui/LoadingButton";
import Alert from "../../components/customMui/Alert";
import standardBizApiClient from "../../util/BizApi/standardBizApiClient";
import "./resetPasswordInitiate.css";

const ResetPasswordInitiate = () => {
    const [submitLoading, setSubmitLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [emailAddress, setEmailAddress] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        const storedAttempts = localStorage.getItem("resetPasswordFailedAttempts");
        const storedLastAttempt = localStorage.getItem("resetPasswordLastAttemptTime");

        if (storedAttempts) {
            const attempts = parseInt(storedAttempts, 10);
            if (storedLastAttempt && Date.now() - parseInt(storedLastAttempt, 10) > 660 * 1000) {
                localStorage.removeItem("resetPasswordFailedAttempts");
                localStorage.removeItem("resetPasswordLastAttemptTime");
                setFailedAttempts(0);
            } else {
                setFailedAttempts(attempts);
            }
        }

        const storedExpiry = localStorage.getItem("resetPasswordLockoutExpiry");
        if (storedExpiry) {
            const remaining = Math.ceil((parseInt(storedExpiry, 10) - Date.now()) / 1000);
            if (remaining > 0) {
                setCountdown(remaining);
            } else {
                localStorage.removeItem("resetPasswordLockoutExpiry");
            }
        }

        const handleStorageChange = (e) => {
            if (e.key === "resetPasswordLockoutExpiry") {
                if (e.newValue) {
                    const remaining = Math.ceil((parseInt(e.newValue, 10) - Date.now()) / 1000);
                    if (remaining > 0) setCountdown(remaining);
                } else {
                    setCountdown(0);
                    setErrorMessage(null);
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            setShowSuccess(false);
            setErrorMessage(<span>Too many attempts. Please wait <span style={{ display: "inline-block", width: "42px", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>{formatTimer(countdown)}</span> and try again.</span>);
            timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        setErrorMessage(null);
                        localStorage.removeItem("resetPasswordLockoutExpiry");
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const formatTimer = (totalSeconds) => {
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
        const seconds = String(totalSeconds % 60).padStart(2, "0");
        return `${minutes}:${seconds}`;
    };

    const startLockout = (seconds) => {
        setCountdown(seconds);
        localStorage.setItem("resetPasswordLockoutExpiry", (Date.now() + seconds * 1000).toString());
    };

    const getLimitSecondsFromError = (errorObj) => {
        const kvdetail = errorObj?.kvdetail || [];
        const limitItem = kvdetail.find((item) => item.key === "limit_seconds_remaining" || item.key === "wait_seconds");
        return Number(limitItem?.value || 0);
    };

    const handleResetPasswordInitiate = async e => {
        e.preventDefault();

        if (countdown > 0) {
            setShowSuccess(false);
            setErrorMessage(<span>Too many attempts. Please wait <span style={{ display: "inline-block", width: "42px", fontVariantNumeric: "tabular-nums", textAlign: "center" }}>{formatTimer(countdown)}</span> and try again.</span>);
            return;
        }

        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);
        localStorage.setItem("resetPasswordFailedAttempts", newFailedAttempts.toString());
        localStorage.setItem("resetPasswordLastAttemptTime", Date.now().toString());

        if (newFailedAttempts === 6) {
            setShowSuccess(false);
            setErrorMessage(null);
            startLockout(60);
            return;
        }
        if (newFailedAttempts >= 9) {
            setShowSuccess(false);
            setErrorMessage(null);
            startLockout(600);
            return;
        }

        setErrorMessage(null);
        setShowSuccess(true);
        setSubmitLoading(true);

        const resetPasswordInitiateRes = await standardBizApiClient.account_reset_password_initiate(emailAddress);
        setSubmitLoading(false);
        return;
    }

    if (showSuccess) {
        return (
            <div className="reset-password-initiate reset-password-initiate-success">
                <h2 className="font-header-light">Reset Password Email Sent</h2>
                <p>
                    WannaPractice Support (support@wannapractice.com) is sending an email to {emailAddress} to verify the reset password.
                </p>
                <p>
                    Please follow the instructions in that email.
                </p>
            </div>
        )
    }

    return (
        <div className="reset-password-initiate">
            <h2 className="font-header-light">Reset Password</h2>
            <form className="reset-password-initiate-form" onSubmit={handleResetPasswordInitiate}>
                {errorMessage ? (<Alert severity="error">{errorMessage}</Alert>) : null}
                <TextField
                    required
                    disabled={submitLoading || countdown > 0}
                    value={emailAddress}
                    onChange={e => setEmailAddress(e.target.value)}
                    id="reset-password-initiate-email-address"
                    type="email"
                    label="Email Address"
                />
                <LoadingButton customProps={{ type: "submit", loading: submitLoading, disabled: countdown > 0 }}>Submit</LoadingButton>
            </form>
        </div>
    )
}

export default ResetPasswordInitiate;
